import { useState } from "react";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import {
  ArrowForward as ArrowForwardIcon,
  Login as LoginIcon,
  Menu as MenuIcon,
  NotificationsNone as NotificationsIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { persistor } from "../redux/store";
import { logoutUser } from "../redux/userReducer";
import NotificationPanel from "./NotificationPanel";
import useNotifications from "../hooks/useNotifications";

function Header() {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.user);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const role = (user?.role || user?.Role || "").toString().toLowerCase();
  const isAdmin = role === "admin";
  const hideAuthAction =
    location.pathname === "/login" || location.pathname === "/register";
  const profileMenuOpen = Boolean(profileAnchorEl);
  const notificationMenuOpen = Boolean(notificationAnchorEl);
  const profileLabel =
    `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
    user?.name ||
    user?.email ||
    "Profile";
  const profileInitial = profileLabel.charAt(0).toUpperCase() || "P";
  const {
    notifications,
    unreadNotificationCount,
    isLoadingNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
  } = useNotifications(user);
  const previewNotifications = notifications.slice(0, 4);

  const adminMenuItems = [
    { label: "Users", path: "/users" },
    { label: "Gallery", path: "/admin/gallery" },
    { label: "Feedback", path: "/admin/feedback" },
    { label: "Events", path: "/events" },
    { label: "Reports", path: "/reports" },
  ];

  const publicMenuItems = [
    { label: "Home", path: "/" },
    { label: "Events", path: "/events" },
    { label: "Association Members", path: "/association-members" },
  ];

  const memberMenuItems = [
    ...publicMenuItems,
    { label: "Entrepreneurs", path: "/entrepreneurs" },
    { label: "Blood Donation", path: "/blood-donation" },
    { label: "Job Referral", path: "/job-referral" },
    { label: "Feedback", path: "/feedback" },
  ];

  const navItems = isAuthenticated ? memberMenuItems : publicMenuItems;

  const handleOpenProfileMenu = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleOpenNotificationMenu = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleCloseProfileMenu = () => {
    setProfileAnchorEl(null);
  };

  const handleCloseNotificationMenu = () => {
    setNotificationAnchorEl(null);
  };

  const handleOpenProfile = () => {
    handleCloseProfileMenu();
    navigate("/profile");
  };

  const handleViewAllNotifications = async () => {
    await markAllNotificationsAsRead();
    handleCloseProfileMenu();
    handleCloseNotificationMenu();
    navigate("/notifications");
  };

  const handleNotificationClick = async (notification) => {
    await markNotificationAsRead(notification?.id);
    handleCloseNotificationMenu();
    navigate(notification?.redirectUrl || "/notifications");
  };

  const handleLogout = async () => {
    handleCloseProfileMenu();
    dispatch(logoutUser());
    await persistor.purge();
    navigate("/login");
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "white",
          color: "#0f172a",
          borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 72, sm: 80 },
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {!isAdmin ? (
                <IconButton
                  onClick={() => setDrawerOpen(true)}
                  aria-label="Open navigation menu"
                  sx={{
                    display: { xs: "inline-flex", md: "none" },
                    color: "inherit",
                  }}
                >
                  <MenuIcon />
                </IconButton>
              ) : null}

              {isAuthenticated && isAdmin ? (
                <IconButton
                  onClick={() => setDrawerOpen(true)}
                  aria-label="Open admin menu"
                  sx={{ color: "inherit" }}
                >
                  <MenuIcon />
                </IconButton>
              ) : null}

              <Box
                component={RouterLink}
                to="/"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                <Box
                  component="img"
                  src="/images/seeds_logo.jpg"
                  alt="Einstein Seeds logo"
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    objectFit: "contain",
                    bgcolor: "#f8fafc",
                    p: 0.75,
                  }}
                />
                <Typography
                  variant="h6"
                  fontWeight={800}
                  sx={{ letterSpacing: 0.4 }}
                >
                  Einstein Seeds
                </Typography>
              </Box>
            </Box>

            {!isAdmin ? (
              <Stack
                direction="row"
                spacing={1}
                sx={{ display: { xs: "none", md: "flex" } }}
              >
                {navItems.map((item) => (
                  <Button
                    key={item.label}
                    component={RouterLink}
                    to={item.path}
                    sx={{
                      color: "inherit",
                      textTransform: "none",
                      fontWeight: 600,
                      borderBottom:
                        location.pathname === item.path
                          ? "2px solid #1d4ed8"
                          : "2px solid transparent",
                      borderRadius: 0,
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Stack>
            ) : null}

            {!isAuthenticated && !hideAuthAction ? (
              <Button
                variant="contained"
                startIcon={<LoginIcon />}
                onClick={() => navigate("/login")}
                sx={{ borderRadius: 999, px: 2.5, textTransform: "none" }}
              >
                Login
              </Button>
            ) : !hideAuthAction ? (
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ alignItems: "center" }}
              >
                {!isAdmin ? (
                  <>
                    <IconButton
                      onClick={handleOpenNotificationMenu}
                      aria-label="Open notifications"
                      aria-controls={
                        notificationMenuOpen ? "notification-menu" : undefined
                      }
                      aria-haspopup="true"
                      aria-expanded={notificationMenuOpen ? "true" : undefined}
                      sx={{ color: "inherit" }}
                    >
                      <Badge
                        badgeContent={unreadNotificationCount}
                        color="error"
                        max={9}
                      >
                        <NotificationsIcon />
                      </Badge>
                    </IconButton>
                    <Menu
                      id="notification-menu"
                      anchorEl={notificationAnchorEl}
                      open={notificationMenuOpen}
                      onClose={handleCloseNotificationMenu}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                      slotProps={{
                        paper: {
                          sx: {
                            width: { xs: "calc(100vw - 32px)", sm: 380 },
                            maxWidth: 380,
                            mt: 1,
                            p: 1.5,
                            borderRadius: 4,
                            boxShadow: "0 24px 50px rgba(15, 23, 42, 0.16)",
                          },
                        },
                      }}
                    >
                      <NotificationPanel
                        notifications={previewNotifications}
                        compact
                        title="Notifications"
                        subtitle={
                          isLoadingNotifications
                            ? "Loading latest updates..."
                            : `${unreadNotificationCount} unread right now.`
                        }
                        actionLabel="View all"
                        actionTo="/notifications"
                        onActionClick={handleViewAllNotifications}
                        onItemClick={handleNotificationClick}
                        emptyMessage={
                          isLoadingNotifications
                            ? "Loading notifications..."
                            : "No notifications available."
                        }
                      />
                    </Menu>
                  </>
                ) : null}

                <IconButton
                  onClick={handleOpenProfileMenu}
                  aria-label="Open profile menu"
                  aria-controls={profileMenuOpen ? "profile-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={profileMenuOpen ? "true" : undefined}
                  sx={{ color: "inherit" }}
                >
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: "#1d4ed8",
                      fontSize: "0.95rem",
                      fontWeight: 700,
                    }}
                  >
                    {profileInitial}
                  </Avatar>
                </IconButton>
                <Menu
                  id="profile-menu"
                  anchorEl={profileAnchorEl}
                  open={profileMenuOpen}
                  onClose={handleCloseProfileMenu}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem onClick={handleOpenProfile}>
                    <PersonIcon sx={{ mr: 1.25, fontSize: 20 }} />
                    Profile
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>Logout</MenuItem>
                </Menu>
              </Stack>
            ) : null}
          </Toolbar>
        </Container>
      </AppBar>

      {!isAdmin ? (
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: {
              width: 280,
              borderTopRightRadius: 24,
              borderBottomRightRadius: 24,
            },
          }}
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="overline" color="text.secondary">
              Navigate
            </Typography>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
              Einstein Seeds
            </Typography>
            <List disablePadding>
              {navItems.map((item) => (
                <ListItemButton
                  key={item.label}
                  component={RouterLink}
                  to={item.path}
                  onClick={() => setDrawerOpen(false)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    bgcolor:
                      location.pathname === item.path
                        ? "rgba(37, 99, 235, 0.08)"
                        : "transparent",
                  }}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              ))}
              {isAuthenticated ? (
                <ListItemButton
                  component={RouterLink}
                  to="/notifications"
                  onClick={() => setDrawerOpen(false)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    bgcolor:
                      location.pathname === "/notifications"
                        ? "rgba(37, 99, 235, 0.08)"
                        : "transparent",
                  }}
                >
                  <ListItemText primary="Notifications" />
                </ListItemButton>
              ) : null}
            </List>
            {!isAuthenticated ? (
              <Button
                variant="contained"
                startIcon={<ArrowForwardIcon />}
                component={RouterLink}
                to="/login"
                onClick={() => setDrawerOpen(false)}
                sx={{ mt: 2, borderRadius: 999, textTransform: "none" }}
              >
                Member Login
              </Button>
            ) : null}
          </Box>
        </Drawer>
      ) : null}

      {isAdmin ? (
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: {
              width: 240,
              mt: { xs: 0, md: "80px" },
              borderTopRightRadius: 20,
              borderBottomRightRadius: 20,
            },
          }}
        >
          <Box sx={{ p: 2.5 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color="text.secondary"
              sx={{ mb: 1.5, textTransform: "uppercase", letterSpacing: 1.2 }}
            >
              Admin Menu
            </Typography>
            <List disablePadding>
              {adminMenuItems.map((item) => (
                <ListItemButton
                  key={item.label}
                  component={RouterLink}
                  to={item.path}
                  onClick={() => setDrawerOpen(false)}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              ))}
            </List>
          </Box>
        </Drawer>
      ) : null}
    </>
  );
}

export default Header;
